import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    if (!CartDrawerComponent.consentEventsBound) {
      document.addEventListener('change', CartDrawerComponent.handleConsentChange);
      document.addEventListener('click', CartDrawerComponent.handleCheckoutClick, true);
      document.addEventListener('submit', CartDrawerComponent.handleCartSubmit, true);
      CartDrawerComponent.consentEventsBound = true;
    }

    this.#syncConsentState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  static consentEventsBound = false;

  static selectors = {
    checkbox: '#service-begin-consent',
    checkoutButton: '#cart-checkout-button',
    cartForm: '#cart-form',
    acceleratedButtons: '#accelerated-checkout-buttons',
    hiddenInputName: 'attributes[consent_service_begin]',
  };

  static getConsentElements() {
    return {
      checkbox: document.querySelector(CartDrawerComponent.selectors.checkbox),
      checkoutBtn: document.querySelector(CartDrawerComponent.selectors.checkoutButton),
      cartForm: document.querySelector(CartDrawerComponent.selectors.cartForm),
      acceleratedButtons: document.querySelector(CartDrawerComponent.selectors.acceleratedButtons),
    };
  }

  static ensureHiddenInput(cartForm) {
    if (!cartForm) return null;

    let hiddenInput = cartForm.querySelector(
      `input[name="${CartDrawerComponent.selectors.hiddenInputName}"]`
    );

    if (!hiddenInput) {
      hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = CartDrawerComponent.selectors.hiddenInputName;
      hiddenInput.value = '';
      cartForm.appendChild(hiddenInput);
    }

    return hiddenInput;
  }

  static applyConsentState() {
    const { checkbox, checkoutBtn, cartForm, acceleratedButtons } =
      CartDrawerComponent.getConsentElements();

    if (!checkbox) return;

    const ok = checkbox.checked;
    const hiddenInput = CartDrawerComponent.ensureHiddenInput(cartForm);

    if (checkoutBtn) {
      checkoutBtn.disabled = !ok;
      checkoutBtn.setAttribute('aria-disabled', String(!ok));
      checkoutBtn.style.pointerEvents = ok ? 'auto' : 'none';
      checkoutBtn.style.opacity = ok ? '1' : '0.5';
    }

    if (acceleratedButtons) {
      acceleratedButtons.hidden = !ok;
    }

    if (hiddenInput) {
      hiddenInput.value = ok ? 'yes' : '';
    }
  }

  static handleConsentChange(event) {
    const target = event.target;
    if (target && target.matches(CartDrawerComponent.selectors.checkbox)) {
      CartDrawerComponent.applyConsentState();
    }
  }

  static handleCheckoutClick(event) {
    const button = event.target.closest(CartDrawerComponent.selectors.checkoutButton);
    if (!button) return;

    const { checkbox } = CartDrawerComponent.getConsentElements();
    if (!checkbox || !checkbox.checked) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      alert('Bitte bestätige zuerst den Hinweis zum vorzeitigen Beginn der Dienstleistung.');
    }
  }

  static handleCartSubmit(event) {
    const form = event.target;
    if (!form || !form.matches(CartDrawerComponent.selectors.cartForm)) return;

    const { checkbox } = CartDrawerComponent.getConsentElements();
    if (!checkbox || !checkbox.checked) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      alert('Bitte bestätige zuerst den Hinweis zum vorzeitigen Beginn der Dienstleistung.');
    }
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();

      requestAnimationFrame(() => this.#syncConsentState());
      setTimeout(() => this.#syncConsentState(), 150);
      setTimeout(() => this.#syncConsentState(), 400);
    }
  };

  #syncConsentState() {
    CartDrawerComponent.applyConsentState();
  }

  open() {
    this.showDialog();

    requestAnimationFrame(() => this.#syncConsentState());
    setTimeout(() => this.#syncConsentState(), 150);
    setTimeout(() => this.#syncConsentState(), 400);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}